import { useState } from "react";
import contactHero from "@/assets/contact-hero.png";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, Clock } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import Logo3D from "@/components/Logo3D";
import GoogleReviews from "@/components/GoogleReviews";

const formFieldStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

const formFieldItem = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const contactJsonLd = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "name": "Contact Dhivisha Architects",
  "description": "Contact Dhivisha Architects, COA-registered firm in Kumbakonam. Call +91 99948 49332, email mail@dhivishaarchitects.com. Mon-Sat, 9 AM-8 PM.",
  "mainEntity": {
    "@type": "ArchitecturalFirm",
    "@id": "https://dhivishaarchitects.com/#organization",
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+919994849332",
        "contactType": "customer service",
        "areaServed": "IN",
        "availableLanguage": ["English", "Tamil"],
      },
      {
        "@type": "ContactPoint",
        "telephone": "+918428077535",
        "contactType": "customer service",
        "areaServed": "IN",
        "availableLanguage": ["English", "Tamil"],
      },
    ],
  },
  "speakable": {
    "@type": "SpeakableSpecification",
    "cssSelector": ["h1", ".contact-info"],
  },
};

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast({ title: "Message Sent!", description: "Our team will get back to you with the best solution." });
    setForm({ name: "", email: "", phone: "", service: "", message: "" });
  };

  return (
    <Layout transparentNav>
      <SEOHead
        title="Contact Us — Registered Architect in Kumbakonam & Delta District"
        description="Contact Dhivisha Architects, COA-registered firm in Kumbakonam, for your next project in the Cauvery Delta region. Call +91 99948 49332, email mail@dhivishaarchitects.com. Mon-Sat, 9 AM-8 PM."
        path="/contact"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(contactJsonLd) }}
      />
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={contactHero} alt="Contact us" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-secondary/70" />
        </div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative z-10 text-center px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">Contact Us</h1>
          <p className="text-lg text-primary-foreground/80">Have questions about our services or want to start a project?</p>
        </motion.div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-16">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-foreground mb-2">Get in Touch</h2>
            <p className="text-muted-foreground mb-8">Share your requirements and our team will get back to you with the best solution.</p>
            <motion.form
              onSubmit={handleSubmit}
              className="space-y-5"
              variants={formFieldStagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.div variants={formFieldItem} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input placeholder="Your Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                <Input type="email" placeholder="Email Address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              </motion.div>
              <motion.div variants={formFieldItem} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input placeholder="Phone Number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <select
                  className="flex min-h-[44px] h-11 w-full rounded border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors"
                  value={form.service}
                  onChange={(e) => setForm({ ...form, service: e.target.value })}
                >
                  <option value="">Select a Service</option>
                  <option>Architectural Design</option>
                  <option>Interior Design</option>
                  <option>Renovation</option>
                  <option>Master Planning</option>
                  <option>3D Visualization</option>
                  <option>Project Management</option>
                  <option>Other</option>
                </select>
              </motion.div>
              <motion.div variants={formFieldItem}>
                <Textarea placeholder="Tell us about your project..." rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required />
              </motion.div>
              <motion.div variants={formFieldItem}>
                <Button type="submit" size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto px-10 active:scale-95 transition-transform">
                  Send Message <Send className="ml-2" size={16} />
                </Button>
              </motion.div>
            </motion.form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="space-y-8"
          >
            <div className="flex justify-center mb-4">
              <Logo3D size="lg" animate glow />
            </div>
            <div>
              <h3 className="text-xl font-bold text-foreground mb-6">Contact Information</h3>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center shrink-0"><Phone size={18} className="text-primary" /></div>
                  <div>
                    <p className="font-medium text-foreground">Phone</p>
                    <p className="text-sm text-muted-foreground">+91 99948 49332</p>
                    <p className="text-sm text-muted-foreground">+91 84280 77535</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center shrink-0"><Mail size={18} className="text-primary" /></div>
                  <div>
                    <p className="font-medium text-foreground">Email</p>
                    <p className="text-sm text-muted-foreground">mail@dhivishaarchitects.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center shrink-0"><MapPin size={18} className="text-primary" /></div>
                  <div>
                    <p className="font-medium text-foreground">Office Address</p>
                    <p className="text-sm text-muted-foreground">No:9, Needamangalam Rd, Elanga Nagar, Srinivas Nagar, Sakkottai, Kumbakonam, Tamil Nadu 612001</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center shrink-0"><Clock size={18} className="text-primary" /></div>
                  <div>
                    <p className="font-medium text-foreground">Visit Between</p>
                    <p className="text-sm text-muted-foreground">Mon – Sat : 9.00 AM – 8.00 PM</p>
                    <p className="text-sm text-muted-foreground">Sunday : Closed</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded overflow-hidden border border-border h-64">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3917.1427395298715!2d79.39458697504449!3d10.95258848920706!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5533ee383f3eff%3A0xfcb8e5be501e4e3b!2sDHIVISHA%20ARCHITECTS%20AND%20INTERIORS!5e0!3m2!1sen!2sin!4v1773296848022!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Dhivisha Architects and Interiors - Kumbakonam Office"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Google Reviews */}
      <GoogleReviews />
    </Layout>
  );
};

export default Contact;
